/**
 * Incident report: what a deputy fills in, which questions each offense needs, and the check that runs before
 * it goes to a supervisor. Every problem is written in plain language and points at the step to fix it.
 * Rules follow SLED's SCIBRS Manual v2025.0 (page refs in comments). The software never fills in a value that
 * goes to the state: the deputy picks every one (p.9 "Prohibition on Defaults").
 */
import { localParts } from '../time.ts';
import {
  FAMILY_RELATIONSHIPS,
  FIREARM_WEAPONS,
  labelOf,
  OFFENSE_BY_CODE,
  PROPERTY_TYPES,
  SIMPLE_ASSAULT_WEAPONS,
  VEHICLE_PROPERTY,
  WEAPONS,
  type OffenseDef,
} from './codes.ts';

export type ReportStep = 'what' | 'where' | 'people' | 'property' | 'narrative';
export type PersonRole = 'victim' | 'suspect' | 'arrestee' | 'witness' | 'complainant' | 'other';

export interface ReportOffense {
  id: string;
  code: string;
  /** Data Element 7: true attempted, false completed, null not answered yet. */
  attempted: boolean | null;
  weapons: string[];
  circumstances: string[];
  /** Data Element 32 (09C only): additional justifiable homicide circumstance. */
  justifiable: string | null;
  criminalActivity: string[];
  gang: string | null;
  suspectedUsing: string[];
  /** Data Element 8A; null until the deputy answers ("None" is 88). */
  bias: string | null;
  entry: 'F' | 'N' | null;
  premises: number | null;
  /** SC data element S6: offense special circumstances (up to 3). */
  special: string[];
}

export interface ReportPerson {
  id: string;
  roles: PersonRole[];
  /** Data Element 25 for victims ('' until picked). */
  type: string;
  /** LEOKA (Data Elements 25A–25C) when the victim is a law enforcement officer. */
  leoka: { activity: string | null; assignment: string | null; ori: string | null } | null;
  /** Suspect not known ("unknown suspect"). */
  unknown: boolean;
  /** Full name as it reads ("First Middle Last Suffix"). Kept in step with the parts below; older reports have only this. */
  name: string;
  /** Name parts, entered separately (Last / First / Middle / Suffix). */
  last: string | null;
  first: string | null;
  middle: string | null;
  suffix: string | null;
  dob: string | null;
  /** Age in years when there's no date of birth (or a range like "25-30"). */
  age: string | null;
  sex: string | null;
  race: string | null;
  ethnicity: string | null;
  resident: string | null;
  phone: string | null;
  address: string | null;
  description: string | null;
  injuries: string[];
  /** For a victim: relationship to each suspect, by suspect id. */
  relationships: Record<string, string>;
  /** Data Element 24: the offenses (ids) this person is a victim of. When only one offense can have a victim, it's that one. */
  victimOf: string[];
  /** Height (inches), weight (lb), hair, eyes, scars/marks/tattoos — mostly for suspects and arrests. */
  height: number | null;
  weight: number | null;
  hair: string | null;
  eyes: string | null;
  marks: string | null;
  /** Physical traits the deputy marked as not known: 'height' | 'weight' | 'hair' | 'eyes'. */
  unknownTraits: string[];
  /** Mugshot / photo stored with the report. */
  photoId: string | null;
  /** For an arrestee. */
  arrest: ArrestInfo | null;
}

export interface LocalCharge {
  statute: string;
  description: string;
  warrant: string | null;
  /** The courts' CDR code when the charge was picked from the list. */
  cdr?: string | null;
}

export interface ArrestInfo {
  at: string | null;
  /** T custody / O on-view / S summoned-cited. */
  type: string | null;
  /** The main charge as a NIBRS offense (what the state counts). */
  charge: string | null;
  warrant: string | null;
  /** Armed with at arrest (up to 2): 01 unarmed … */
  armed: string[];
  /** Under 18: H handled within department, R referred to court/other authorities. */
  juvenile: string | null;
  /** SC S9 / S10, required when the arrest offense is 35A drugs. */
  drugActivity: string | null;
  drugType: string | null;
  bookingNumber: string | null;
  facility: string | null;
  /** Every charge as written on the warrant / booking (SC statute + words). */
  localCharges: LocalCharge[];
}

/** Arrest time, type and offense are the deputy's to pick (no defaults). */
export const newArrest = (at: string | null = null, charge: string | null = null): ArrestInfo => ({ at, type: null, charge, warrant: null, armed: [], juvenile: null, drugActivity: null, drugType: null, bookingNumber: null, facility: null, localCharges: [] });

export interface ReportProperty {
  id: string;
  loss: string;
  type: string;
  description: string;
  value: number | null;
  valueUnknown: boolean;
  quantity: number | null;
  make: string | null;
  model: string | null;
  serial: string | null;
  color: string | null;
  plate: string | null;
  vin: string | null;
  year: string | null;
  owner: string | null;
  drug: { type: string | null; qty: number | null; unit: string | null } | null;
  /** Data Element 17: date recovered (YYYY-MM-DD), for recovered property. */
  recoveredOn: string | null;
  /** Booked into the evidence room (gets a barcode when the report is sent). */
  evidence: boolean;
}

export interface IncidentReport {
  v: 1;
  occurredFrom: string | null;
  occurredTo: string | null;
  /** Data Element 3: 'date' — date not known (the report date is sent with an R); 'hour' — the day is known, the hour isn't. */
  occurredUnknown: 'date' | 'hour' | null;
  reportedAt: string | null;
  /** Data Element 2A cargo theft (only asked for the offenses that can be cargo theft). */
  cargoTheft: boolean | null;
  location: { line1: string; city: string | null; state: string | null; zip: string | null; lat: number | null; lng: number | null; type: string | null; detail: string | null };
  offenses: ReportOffense[];
  people: ReportPerson[];
  property: ReportProperty[];
  narrative: string;
  /** 'closed' = SC case status C, administrative closure (investigation closed, not cleared); 'unfounded' = U. */
  clearance: { status: 'open' | 'arrest' | 'exceptional' | 'unfounded' | 'closed'; reason: string | null; date: string | null };
  /** Related calls / case numbers / BOLO etc. */
  related: string | null;
  /** Narcan (naloxone): used on this call, who gave it, and whether the deputy filled in the Narcan use form. */
  narcan?: NarcanInfo | null;
  /** The victim is society / the public (drugs, weapons, DUI… — crimes against society), not a person or business. */
  societyVictim?: boolean;
}

export type NarcanGiver = 'me' | 'deputy' | 'ems' | 'other';
export interface NarcanInfo {
  used: boolean | null;
  givenBy: NarcanGiver | null;
  doses: number | null;
  /** The deputy says they filled in the state Narcan use form (and kept a copy). */
  formDone: boolean;
}
/** Ask about Narcan only on an overdose call, or when the narrative mentions Narcan or an overdose (or it was already answered). */
export function narcanRelevant(r: IncidentReport): boolean {
  if (r.narcan?.used != null) return true;
  if (r.offenses.some((o) => o.code === 'NC-OVERDOSE')) return true;
  return /\b(narcan|naloxone|overdos\w*|od(?:'?d|ed)?)\b|\bo\.d\./i.test(r.narrative ?? '');
}
/** The Narcan use form deputies fill in after giving Narcan. */
export const NARCAN_FORM_URL = 'https://sc.readyop.com/fs/4cyr/d967';
export const NARCAN_GIVERS: { code: NarcanGiver; label: string }[] = [
  { code: 'me', label: 'I gave it' },
  { code: 'deputy', label: 'Another deputy' },
  { code: 'ems', label: 'EMS / fire' },
  { code: 'other', label: 'Someone else (family, bystander)' },
];

export const emptyReport = (now: Date): IncidentReport => ({
  v: 1,
  occurredFrom: null,
  occurredTo: null,
  occurredUnknown: null,
  reportedAt: now.toISOString(),
  cargoTheft: null,
  location: { line1: '', city: null, state: 'SC', zip: null, lat: null, lng: null, type: null, detail: null },
  offenses: [],
  people: [],
  property: [],
  narrative: '',
  clearance: { status: 'open', reason: null, date: null },
  related: null,
});

export const newOffense = (id: string, code: string): ReportOffense => ({
  id,
  code,
  attempted: null,
  weapons: [],
  circumstances: [],
  justifiable: null,
  criminalActivity: [],
  gang: null,
  suspectedUsing: [],
  bias: null,
  entry: null,
  premises: null,
  special: [],
});

export const newPerson = (id: string, role: PersonRole): ReportPerson => ({
  id,
  roles: [role],
  type: '',
  leoka: null,
  unknown: false,
  name: '',
  last: null,
  first: null,
  middle: null,
  suffix: null,
  dob: null,
  age: null,
  sex: null,
  race: null,
  ethnicity: null,
  resident: null,
  phone: null,
  address: null,
  description: null,
  injuries: [],
  relationships: {},
  victimOf: [],
  height: null,
  weight: null,
  hair: null,
  eyes: null,
  marks: null,
  unknownTraits: [],
  photoId: null,
  arrest: role === 'arrestee' ? newArrest() : null,
});

/** "First Middle Last Suffix" from the parts. */
export function joinName(p: { first?: string | null; middle?: string | null; last?: string | null; suffix?: string | null }): string {
  return [p.first, p.middle, p.last, p.suffix].map((x) => (x ?? '').trim()).filter(Boolean).join(' ');
}

const SUFFIXES = /^(jr\.?|sr\.?|ii|iii|iv|v)$/i;
/** Best guess at the parts of a typed name: "Doe, John A." or "John A. Doe Jr." */
export function splitName(name: string): { last: string | null; first: string | null; middle: string | null; suffix: string | null } {
  const n = name.trim().replace(/\s+/g, ' ');
  if (!n) return { last: null, first: null, middle: null, suffix: null };
  let last: string, rest: string[];
  let suffix: string | null = null;
  if (n.includes(',')) {
    const [l, ...r] = n.split(',');
    const lw = l!.trim().split(' ');
    if (lw.length > 1 && SUFFIXES.test(lw.at(-1)!)) suffix = lw.pop()!;
    last = lw.join(' ');
    rest = r.join(' ').trim().split(' ').filter(Boolean);
    if (rest.length && SUFFIXES.test(rest.at(-1)!)) suffix = rest.pop()!;
  } else {
    const w = n.split(' ');
    if (w.length > 1 && SUFFIXES.test(w.at(-1)!)) suffix = w.pop()!;
    if (w.length === 1) return { last: w[0]!, first: null, middle: null, suffix };
    last = w.pop()!;
    rest = w;
  }
  return { last: last || null, first: rest[0] ?? null, middle: rest.slice(1).join(' ') || null, suffix };
}

/** A person's name parts, falling back to splitting the full name on older reports. */
export function nameParts(p: Pick<ReportPerson, 'name' | 'last' | 'first' | 'middle' | 'suffix'>) {
  if (p.last || p.first || p.middle || p.suffix) return { last: p.last ?? null, first: p.first ?? null, middle: p.middle ?? null, suffix: p.suffix ?? null };
  return splitName(p.name ?? '');
}

export const newProperty = (id: string, loss: string): ReportProperty => ({
  id,
  loss,
  evidence: loss === '6',
  type: '',
  description: '',
  value: null,
  valueUnknown: false,
  quantity: 1,
  make: null,
  model: null,
  serial: null,
  color: null,
  plate: null,
  vin: null,
  year: null,
  owner: null,
  drug: null,
  recoveredOn: null,
});

// ─── Which questions each offense needs ─────────────────────────────────────

const has = (codes: string[]) => (c: string) => codes.includes(c);
const WEAPON = has(['09A', '09B', '09C', '100', '11A', '11B', '11C', '11D', '120', '13A', '13B', '210', '520', '64A', '64B', '980']);
/** Offenses that can only be completed (pp.19, 26, 31). */
export const COMPLETED_ONLY = ['09A', '09B', '09C', '13A', '13B', '13C'];
/** Data Element 2A: offenses that can be cargo theft (FBI NIBRS list; SC p.106 gives 120, 220, 26F as examples). */
export const CARGO_OFFENSES = ['120', '210', '220', '23D', '23F', '23H', '240', '26A', '26B', '26C', '26E', '26F', '26G', '270', '510'];
/** Victim type L only for these (p.157). */
export const OFFICER_VICTIM_OFFENSES = ['09A', '13A', '13B', '13C'];
const CIRCUMSTANCE = has(['09A', '09B', '09C', '13A']);
const GANG = has(['09A', '09B', '100', '11A', '11B', '11C', '11D', '120', '13A', '13B', '13C']);
const ACTIVITY = has(['250', '280', '35A', '35B', '39C', '370', '520', '720']);
const INJURY = has(['100', '11A', '11B', '11C', '11D', '120', '13A', '13B', '210', '64A', '64B']);

/** Offenses that need a property entry (p.134), and the loss types the state accepts for them. */
const PROPERTY_OFFENSES = ['100', '120', '200', '210', '220', '23A', '23B', '23C', '23D', '23E', '23F', '23G', '23H', '240', '250', '26A', '26B', '26C', '26D', '26E', '26F', '26G', '270', '280', '290', '35A', '35B', '39A', '39B', '39C', '39D', '510'];
export function expectedLoss(code: string, attempted = false): { loss: string[]; ask: string } | null {
  if (!PROPERTY_OFFENSES.includes(code)) return null;
  // An attempt reports property loss "None" (p.134; 210 p.67; 35A p.84).
  if (attempted) return { loss: ['1'], ask: 'a property entry marked “None” (it was only attempted)' };
  if (code === '100') return { loss: ['7', '1'], ask: 'the ransom paid (or mark “None” if no ransom was paid)' };
  if (code === '220') return { loss: ['7', '5', '1'], ask: 'what was taken (or mark “None” if nothing was)' };
  if (['120', '210', '23A', '23B', '23C', '23D', '23E', '23F', '23G', '23H', '240', '26A', '26B', '26C', '26D', '26E', '26F', '26G', '270', '510'].includes(code)) {
    return { loss: ['7', '5'], ask: 'what was taken' };
  }
  if (code === '280') return { loss: ['5', '1'], ask: 'the stolen property recovered (or “None” if it’s already counted on the theft report)' };
  if (code === '200') return { loss: ['2'], ask: 'what was burned' };
  if (code === '290') return { loss: ['4'], ask: 'what was damaged' };
  if (code === '250') return { loss: ['3', '5', '6'], ask: 'what was forged or counterfeited' };
  if (['39A', '39B', '39C', '39D'].includes(code)) return { loss: ['6', '1'], ask: 'the gambling money or equipment seized' };
  if (code === '35A' || code === '35B') return { loss: ['6', '1'], ask: code === '35A' ? 'the drugs seized (or mark “None” with the suspected drug type if nothing was seized)' : 'the equipment seized' };
  return null;
}

export interface OffenseNeeds {
  def: OffenseDef | undefined;
  weapon: boolean;
  /** Simple assault only allows hands/fists/none/other/unknown. */
  weaponChoices: string[] | null;
  circumstances: 'assault' | 'negligent' | 'justifiable' | null;
  /** Completed vs attempted is a real question (not for murder / assaults, which are always completed). */
  askAttempted: boolean;
  cargo: boolean;
  gang: boolean;
  activity: 'drugs' | 'animal' | null;
  entry: boolean;
  injury: boolean;
  relationship: boolean;
  property: ReturnType<typeof expectedLoss>;
}

export function needsFor(o: Pick<ReportOffense, 'code' | 'attempted'>): OffenseNeeds {
  const c = o.code;
  const def = OFFENSE_BY_CODE.get(c);
  return {
    def,
    weapon: WEAPON(c),
    weaponChoices: c === '13B' ? SIMPLE_ASSAULT_WEAPONS : null,
    circumstances: c === '09B' ? 'negligent' : c === '09C' ? 'justifiable' : CIRCUMSTANCE(c) ? 'assault' : null,
    askAttempted: def?.group === 'A' && !COMPLETED_ONLY.includes(c) && !['979', '978', '992'].includes(c),
    cargo: CARGO_OFFENSES.includes(c),
    gang: GANG(c),
    activity: c === '720' ? 'animal' : ACTIVITY(c) ? 'drugs' : null,
    entry: c === '220',
    injury: INJURY(c),
    relationship: def?.against === 'person' || c === '120',
    property: expectedLoss(c, o.attempted === true),
  };
}

// ─── People helpers ─────────────────────────────────────────────────────────

/** Age in whole years on a date, from a date of birth (YYYY-MM-DD). */
export function ageOn(dob: string | null, on: Date): number | null {
  if (!dob || !/^\d{4}-\d{2}-\d{2}$/.test(dob)) return null;
  const [y, m, d] = dob.split('-').map(Number) as [number, number, number];
  let age = on.getUTCFullYear() - y;
  if (on.getUTCMonth() + 1 < m || (on.getUTCMonth() + 1 === m && on.getUTCDate() < d)) age--;
  return age >= 0 && age < 130 ? age : null;
}

export const isVictim = (p: ReportPerson) => p.roles.includes('victim');

/** Offenses that have victims (DE 24): Group A crimes against persons or property, and the SC codes for a missing person, suicide, fire or prowler. */
export function victimOffenses(r: Pick<IncidentReport, 'offenses'>) {
  return r.offenses.filter((o) => {
    const d = OFFENSE_BY_CODE.get(o.code);
    return d?.group === 'A' && (d.against === 'person' || d.against === 'property' || ['979', '980', '978', '992'].includes(o.code));
  });
}
/** The offenses this victim is linked to: what the deputy picked, or the only one there is. */
export function offensesOfVictim(r: Pick<IncidentReport, 'offenses'>, v: ReportPerson): ReportOffense[] {
  const can = victimOffenses(r);
  const picked = can.filter((o) => (v.victimOf ?? []).includes(o.id));
  if (picked.length) return picked;
  return can.length === 1 ? can : [];
}
export const isSuspect = (p: ReportPerson) => p.roles.includes('suspect') || p.roles.includes('arrestee');
export const isArrestee = (p: ReportPerson) => p.roles.includes('arrestee');
const isPersonVictim = (p: ReportPerson) => p.type === 'I' || p.type === 'L';
/** A business, bank, church… as the victim (picked a type other than person / officer). */
export const isOrgVictim = (p: ReportPerson) => isVictim(p) && !!p.type && !isPersonVictim(p);
/** Ages the state takes (pp.160, 172, 182): 1–98, a range like 25-30, 99 (over 98), 00 / unknown, and for victims NN / NB / BB (newborns). */
export const AGE_PATTERN = /^(\d{1,2}(-\d{1,2})?|nn|nb|bb|unknown|00)$/i;
const hasAge = (p: ReportPerson) => !!p.dob || !!(p.age && AGE_PATTERN.test(p.age.trim()));
/** Lowest age a person could be (DOB, a number, or the low end of a range); null when unknown. */
export function minAge(p: ReportPerson, on: Date): number | null {
  if (p.dob) return ageOn(p.dob, on);
  const m = (p.age ?? '').trim().match(/^(\d{1,2})(?:-\d{1,2})?$/);
  if (m) return Number(m[1]);
  return /^(nn|nb|bb)$/i.test((p.age ?? '').trim()) ? 0 : null;
}
export const personLabel = (p: ReportPerson) => (p.unknown ? 'Unknown suspect' : p.name.trim() || (isOrgVictim(p) ? 'Unnamed victim' : 'Unnamed person'));

// ─── The check before submitting ────────────────────────────────────────────

export interface ReportIssue {
  level: 'error' | 'warning';
  step: ReportStep;
  /** The offense / person / property id to jump to. */
  ref?: string;
  message: string;
}

/** The “Information only / no crime” code. */
export const INFO_ONLY = 'NC-INFO';

export function checkReport(r: IncidentReport, now: Date): ReportIssue[] {
  const out: ReportIssue[] = [];
  const err = (step: ReportStep, message: string, ref?: string) => out.push({ level: 'error', step, message, ref });
  const warn = (step: ReportStep, message: string, ref?: string) => out.push({ level: 'warning', step, message, ref });
  const def = (c: string) => OFFENSE_BY_CODE.get(c);
  const codes = r.offenses.map((o) => o.code);
  const victims = r.people.filter(isVictim);
  const suspects = r.people.filter(isSuspect);
  const arrestees = r.people.filter(isArrestee);

  // What happened
  if (!r.offenses.length) err('what', 'Pick what happened (at least one offense, or “Information only”).');
  const seen = new Set<string>();
  for (const o of r.offenses) {
    const n = needsFor(o);
    const name = n.def?.plain ?? o.code;
    if (!n.def) err('what', `Unknown offense code ${o.code}.`, o.id);
    if (seen.has(o.code)) err('what', `${name} is listed twice. List each offense once.`, o.id);
    seen.add(o.code);
    if (n.def?.group === 'NC') continue;
    if (n.def?.group === 'A') {
      if (n.askAttempted && o.attempted == null) err('what', `${name}: was it completed or only attempted?`, o.id);
      if (!n.askAttempted && o.attempted === true) err('what', `${name} is always reported as completed (an attempted murder is an aggravated assault).`, o.id);
      if (!o.suspectedUsing.length) err('what', `${name}: was the suspect using alcohol, drugs or a computer/phone? (Pick “None of these” if not.)`, o.id);
      if (o.suspectedUsing.includes('N') && o.suspectedUsing.length > 1) err('what', `${name}: “None of these” can’t be picked with anything else.`, o.id);
      if (!o.bias) err('what', `${name}: was it a bias (hate) crime? Pick “None (no bias)” if not.`, o.id);
      else if (o.bias !== '88' && o.bias !== '99') warn('what', `${name} is marked as a bias (hate) crime. Make sure the narrative explains why; SLED needs a paper copy.`, o.id);
    }
    if (n.weapon && !o.weapons.length) err('what', `${name}: what weapon or force was used? (Pick “None” if none.)`, o.id);
    if (o.weapons.includes('99') && o.weapons.length > 1) err('what', `${name}: “None” can’t be picked with a weapon.`, o.id);
    if (n.weaponChoices && o.weapons.some((w) => !n.weaponChoices!.includes(w))) {
      err('what', `Simple assault can’t involve a ${labelOf(WEAPONS, o.weapons.find((w) => !n.weaponChoices!.includes(w)))!.toLowerCase()}. If a weapon was used, make it aggravated assault.`, o.id);
    }
    if (o.weapons.length > 3) err('what', `${name}: pick at most 3 weapons.`, o.id);
    // Circumstances (Data Element 31/32, pp.165–168)
    if (n.circumstances && !o.circumstances.length) err('what', `${name}: what were the circumstances?`, o.id);
    if (n.circumstances === 'assault') {
      if (o.circumstances.length > 2) err('what', `${name}: pick at most 2 circumstances.`, o.id);
      if (o.circumstances.includes('10') && o.circumstances.length > 1) err('what', `${name}: “Unknown circumstances” can’t be picked with others.`, o.id);
      if (o.code === '13A' && o.circumstances.includes('07')) err('what', 'Aggravated assault: “Mercy killing” is only for a homicide.', o.id);
    }
    if ((n.circumstances === 'negligent' || n.circumstances === 'justifiable') && o.circumstances.length > 1) err('what', `${name}: pick only one circumstance.`, o.id);
    if (n.circumstances === 'negligent' && ['30', '31', '32', '33'].includes(o.circumstances[0] ?? '') && !o.weapons.some((w) => FIREARM_WEAPONS.includes(w))) err('what', `${name}: that circumstance is only for a firearm. Pick the firearm as the weapon, or “Other negligent killing”.`, o.id);
    if (n.circumstances === 'justifiable' && !o.justifiable) err('what', 'Justifiable homicide: what happened (criminal attacked an officer, a civilian, fled…)?', o.id);
    if (n.activity && !o.criminalActivity.length) err('what', `${name}: what kind of activity was it (possessing, selling, using…)?`, o.id);
    if (n.gang && !o.gang) err('what', `${name}: was a gang involved? (Pick “None / unknown” if not.)`, o.id);
    if (n.entry && !o.entry) err('what', 'Burglary: was entry forced or not forced?', o.id);
    const multiUnit = n.entry && ['14', '19'].includes(r.location.type ?? '');
    if (multiUnit && !o.premises) err('what', 'Burglary at a hotel or storage facility: how many rooms or units were entered?', o.id);
    if (multiUnit && o.premises != null && (o.premises < 1 || o.premises > 99 || !Number.isInteger(o.premises))) err('what', 'Rooms or units entered must be a whole number from 1 to 99.', o.id);
    if (o.code === '520' && o.weapons.some((w) => ['40', '99'].includes(w))) err('what', 'Weapons violation: pick the weapon involved (a gun, knife, etc.).', o.id);
    // Special circumstances (S6, pp.131–133)
    if (o.special.length > 3) err('what', `${name}: pick at most 3 special circumstances.`, o.id);
    if (o.special.includes('C') && o.code !== '120') err('what', '“Carjacking” only goes with robbery.', o.id);
    if (o.special.includes('S') && (!codes.includes('980') || r.clearance.status !== 'exceptional' || r.clearance.reason !== 'A')) err('what', '“Offender committed suicide” needs the suicide (980) on the report and the case cleared exceptionally for death of the offender.', o.id);
    if (o.special.includes('H') && (!o.bias || o.bias === '88' || o.bias === '99')) err('what', `${name}: marked hate/bias motivated, so pick the bias.`, o.id);
  }
  const reportable = r.offenses.filter((o) => def(o.code)?.group === 'A');
  const groupB = r.offenses.filter((o) => def(o.code)?.group === 'B');
  const informationOnly = r.offenses.length > 0 && !reportable.length && !groupB.length;
  if (codes.includes(INFO_ONLY) && r.offenses.length > 1) err('what', '“Information only” can’t be on a report with an offense. Take off one or the other.');
  if (informationOnly && arrestees.length) err('people', 'An information-only report has no charges, so no one can be listed as arrested. Pick the offense, or change their role.');
  if (r.societyVictim) {
    const societyOnly = reportable.length > 0 && reportable.every((o) => def(o.code)!.against === 'society');
    if (!reportable.some((o) => def(o.code)!.against === 'society'))
      err('people', '“Victim is society / the public” is only for crimes against society (drugs, weapons, DUI, gambling…). Untick it, or add who was harmed.');
    else if (!societyOnly) warn('people', 'Society / the public is the victim of the crimes against society here; the other offenses still need their own victim.');
  }
  if (reportable.some((o) => needsFor(o).cargo) && r.cargoTheft == null) err('what', 'Was this cargo theft (goods taken from a commercial shipment)? Answer yes or no.');
  if (codes.includes('978') && codes.includes('200')) err('what', 'A fire is either arson (set on purpose) or a suspicious fire (cause not known), not both.');

  // Where & when (Data Element 3, p.106)
  const from = r.occurredFrom ? new Date(r.occurredFrom) : null;
  if (r.occurredUnknown !== 'date') {
    if (!from || Number.isNaN(+from)) err('where', 'When did it happen? Enter the date and time (or tick that the date isn’t known).');
    else if (+from > +now + 5 * 60_000) err('where', 'The date it happened is in the future.');
  }
  if (r.occurredTo && from && +new Date(r.occurredTo) < +from) err('where', '“Happened until” is before “happened from”.');
  if (r.reportedAt && from && r.occurredUnknown !== 'date' && +new Date(r.reportedAt) < +from - 60_000) warn('where', 'It was reported before it happened. Check the dates.');
  if (!r.reportedAt) err('where', 'When was it reported?');
  if (!r.location.line1.trim() && (r.location.lat == null || r.location.lng == null)) err('where', 'Where did it happen? Enter the address, or use your location.');
  if (!r.location.type && !informationOnly) err('where', 'What kind of place was it (home, road, store…)?');

  // People
  for (const p of r.people) if (!p.roles.length) err('people', `${personLabel(p)}: pick their role (victim, suspect, witness…).`, p.id);
  const needsVictim = reportable.some((o) => ['person', 'property'].includes(def(o.code)!.against) || ['979', '980'].includes(o.code));
  if (needsVictim && !victims.length) err('people', 'Add the victim (a person, business, or other).');
  const personCrimes = reportable.filter((o) => def(o.code)!.against === 'person' || ['979', '980'].includes(o.code));
  if (personCrimes.length && !victims.some(isPersonVictim)) err('people', `${def(personCrimes[0]!.code)!.plain} needs a victim who is a person.`);
  if (reportable.some((o) => !def(o.code)!.notCrime || o.code === '980') && !suspects.length) err('people', 'Add the suspect. If nobody is known, tick “Offender unknown”.');
  if (codes.includes('980') && !r.people.some((p) => isVictim(p) && isSuspect(p))) err('people', 'Suicide: the person is reported as both the victim and the offender. Give them both roles.');
  if (suspects.filter((s) => s.unknown).length > 1) warn('people', 'Only one “Unknown suspect” is needed when nothing is known. If you know there were two, add them as suspects with what you know (sex, race, age can be “Unknown”).');
  const incidentDay = from ?? (r.reportedAt ? new Date(r.reportedAt) : now);
  // Data Element 24: every victim is tied to the offenses against them, and every such offense has a victim.
  const linkable = victimOffenses(r);
  const linked = new Map(victims.map((v) => [v.id, offensesOfVictim(r, v)]));
  if (victims.length) {
    for (const o of linkable) {
      const has = victims.some((v) => linked.get(v.id)!.some((x) => x.id === o.id));
      if (!has && linkable.length > 1) err('people', `${def(o.code)!.plain}: who was the victim? Tick it on the victim.`, o.id);
    }
  }
  for (const v of victims) {
    const label = personLabel(v);
    if (!v.type) {
      err('people', `${label}: who is the victim (a person, business, officer, bank, church…)?`, v.id);
      continue;
    }
    const mine = linked.get(v.id)!;
    const mineCodes = mine.map((o) => o.code);
    if (linkable.length > 1 && !mine.length) err('people', `${label}: which offenses was this person the victim of?`, v.id);
    if (!isPersonVictim(v)) {
      const bad = mine.find((o) => def(o.code)!.against === 'person');
      if (bad) err('people', `${label}: ${def(bad.code)!.plain.toLowerCase()} needs a victim who is a person, not a business or organization.`, v.id);
    }
    if (mineCodes.includes('09A') && mineCodes.includes('13A')) err('people', `${label}: one victim can’t be both murdered and aggravated-assaulted. Untick one.`, v.id);
    if (mineCodes.includes('11D') && mineCodes.some((c) => ['11A', '11B', '11C'].includes(c))) err('people', `${label}: criminal sexual contact and rape can’t both be for the same victim.`, v.id);
    if (v.type === 'L') {
      if (!mineCodes.some((c) => OFFICER_VICTIM_OFFENSES.includes(c)) && !(linkable.length > 1 && !mine.length)) err('people', `${label}: “Law enforcement officer” as the victim is only for murder or assault. For other crimes, pick “Person”.`, v.id);
      for (const o of mine.filter((o) => ['13A', '09A'].includes(o.code))) if (!o.circumstances.includes('02')) err('what', `${def(o.code)!.plain}: the victim is an officer, so pick “Assault on a law enforcement officer” in the circumstances.`, o.id);
      for (const o of mine) if (!o.special.includes('P')) err('what', `${def(o.code)!.plain}: the victim is an officer, so add the special circumstance “Victim is a police officer”.`, o.id);
      if (!v.leoka?.activity) err('people', `${label}: what was the officer doing (call type)?`, v.id);
      if (!v.leoka?.assignment) err('people', `${label}: how was the officer assigned (alone in uniform, two-officer car…)?`, v.id);
      const a = minAge(v, incidentDay);
      if (a != null && (a < 17 || a > 98)) err('people', `${label}: an officer’s age must be 17 to 98.`, v.id);
    }
    if (isPersonVictim(v)) {
      if (!v.unknown && !v.name.trim()) err('people', 'A victim needs a name.', v.id);
      if (!hasAge(v)) err('people', `${label}: date of birth or age is needed for a victim (“unknown” is allowed).`, v.id);
      if (!v.sex) err('people', `${label}: sex is needed for a victim.`, v.id);
      if (!v.race) err('people', `${label}: race is needed for a victim.`, v.id);
      if (v.type === 'I' && !v.ethnicity) warn('people', `${label}: ethnicity isn’t filled in.`, v.id);
      if (mine.some((o) => needsFor(o).injury) && !v.injuries.length) err('people', `${label}: was the victim hurt? (Pick “None” if not.)`, v.id);
      if (v.injuries.includes('N') && v.injuries.length > 1) err('people', `${label}: “None” can’t be picked with an injury.`, v.id);
      if (mine.some((o) => needsFor(o).relationship)) {
        for (const s of suspects.filter((s) => !s.unknown && s.id !== v.id)) {
          if (!v.relationships[s.id]) err('people', `${label}: how is the victim related to ${personLabel(s)}? (Stranger, acquaintance, spouse…)`, v.id);
        }
      }
      const age = minAge(v, incidentDay);
      if (age != null && age <= 10 && mineCodes.some((c) => c === '36A' || c === '36B')) err('people', `${label} is 10 or younger: report it as rape or criminal sexual contact, not incest or statutory rape.`, v.id);
      if (mineCodes.includes('11A') && v.sex && ['M', 'F'].includes(v.sex) && suspects.filter((s) => !s.unknown).length && !suspects.some((s) => !s.unknown && s.sex && s.sex !== v.sex)) {
        warn('people', `${label}: rape (sexual intercourse) needs at least one offender of the opposite sex. Check the offense (sodomy or object may fit).`, v.id);
      }
    } else if (!v.name.trim()) err('people', 'Enter the name of the business or organization that’s the victim.', v.id);
  }
  for (const s of suspects) {
    if (s.unknown) continue;
    const label = personLabel(s);
    if (!s.name.trim() && isArrestee(s)) err('people', 'An arrested person needs a name.', s.id);
    // Known offenders: age, sex and race are reported (pp.172–175); "Unknown" is a fine answer.
    if (!hasAge(s)) err('people', `${label}: date of birth or approximate age (or “unknown”).`, s.id);
    if (!s.sex) err('people', `${label}: sex (or “Unknown”).`, s.id);
    if (!s.race) err('people', `${label}: race (or “Unknown”).`, s.id);
    if (isArrestee(s) && s.sex === 'U') err('people', `${label}: an arrested person’s sex can’t be “Unknown”.`, s.id);
  }
  for (const a of arrestees) {
    if (a.unknown) err('people', 'An arrested person can’t be an “unknown suspect”.', a.id);
    if (!a.arrest?.at) err('people', `${personLabel(a)}: when were they arrested?`, a.id);
    if (!a.arrest?.type) err('people', `${personLabel(a)}: how were they arrested (custody, on-view, cited)?`, a.id);
    const charge = a.arrest?.charge ? def(a.arrest.charge) : undefined;
    if (!a.arrest?.charge) err('people', `${personLabel(a)}: what were they arrested for?`, a.id);
    else if (!charge || charge.group === 'NC') err('people', `${personLabel(a)}: pick a state offense for the arrest.`, a.id);
    else if (charge.noArrest || (charge.code === '980' && !r.offenses.some((o) => o.code === '980' && o.attempted === true))) err('people', `${personLabel(a)}: no arrest can be reported for ${charge.plain.toLowerCase()}.`, a.id);
    else if (charge.group === 'A' && !codes.includes(charge.code)) err('people', `${personLabel(a)}: arrested for ${charge.plain.toLowerCase()}, which isn’t one of the offenses on this report. Add it, or pick an offense that is.`, a.id);
    if (a.arrest && !a.arrest.armed.length) err('people', `${personLabel(a)}: were they armed when arrested? (Pick “Unarmed” if not.)`, a.id);
    if (a.arrest?.armed.includes('01') && a.arrest.armed.length > 1) err('people', `${personLabel(a)}: “Unarmed” can’t be picked with a weapon.`, a.id);
    if (a.arrest?.charge === '35A') {
      if (!a.arrest.drugActivity) err('people', `${personLabel(a)}: drug arrest — was it for possessing, selling, using…?`, a.id);
      if (!a.arrest.drugType) err('people', `${personLabel(a)}: drug arrest — what kind of drug?`, a.id);
    }
    const age = minAge(a, a.arrest?.at ? new Date(a.arrest.at) : now);
    if (age != null && age < 18 && !a.arrest?.juvenile) err('people', `${personLabel(a)} is under 18: was it handled in the department or referred to family court?`, a.id);
    if (a.arrest?.type === 'T' && !a.arrest.bookingNumber) warn('people', `${personLabel(a)}: add the booking number from the jail.`, a.id);
    for (const c of a.arrest?.localCharges ?? []) if (!c.description.trim()) err('people', `${personLabel(a)}: a charge is missing its description.`, a.id);
  }
  if (groupB.length && !arrestees.length && !reportable.length) {
    warn('people', `${def(groupB[0]!.code)!.plain} is only reported to the state when someone is arrested. Without an arrest it stays in your records only.`);
  }

  // Property (one message per thing to add: "Burglary and theft: add what was taken")
  const missingProp = new Map<string, { names: string[]; ref: string }>();
  for (const o of reportable) {
    const n = needsFor(o);
    if (!n.property) continue;
    const items = r.property.filter((p) => n.property!.loss.includes(p.loss));
    if (!items.length) {
      const m = missingProp.get(n.property.ask);
      if (m) m.names.push(n.def!.plain);
      else missingProp.set(n.property.ask, { names: [n.def!.plain], ref: o.id });
    }
    if (o.code === '240' && o.attempted !== true && !r.property.some((p) => p.loss === '7' && VEHICLE_PROPERTY.includes(p.type))) err('property', 'Stolen vehicle: add the vehicle that was stolen (car, truck, motorcycle…).', o.id);
    if (o.code === '35A' && o.attempted !== true) {
      const seized = r.property.some((p) => p.loss === '6' && p.type === '10');
      const none = r.property.find((p) => p.loss === '1');
      if (!seized && !none) err('property', 'Drugs: add the drugs that were seized, or a “None” entry with the suspected drug type if nothing was seized.', o.id);
      if (!seized && none && !none.drug?.type) err('property', 'Drugs: nothing was seized, so pick the suspected drug type on the “None” entry.', none.id);
    }
  }
  for (const [ask, m] of missingProp) err('property', `${m.names.join(' and ')}: add ${ask}.`, m.ref);
  if (codes.includes('978') && r.property.length) err('property', 'A suspicious fire can’t have property listed. If it turns out to be arson, change the offense to arson.');
  const seizedDrugTypes = new Set(r.property.filter((p) => p.type === '10' && p.loss === '6' && p.drug?.type).map((p) => p.drug!.type));
  if (seizedDrugTypes.size > 3 && !seizedDrugTypes.has('X')) warn('property', 'More than 3 kinds of drugs were seized. The state takes up to 3, or “More than 3 drug types”.');
  for (const p of r.property) {
    const what = p.description.trim() || labelOf(PROPERTY_TYPES, p.type) || 'An item';
    const noneOrUnknown = p.loss === '1' || p.loss === '8';
    if (!p.loss) err('property', `${what}: was it stolen, damaged, recovered, seized…?`, p.id);
    if (!p.type && !noneOrUnknown) err('property', `${what}: pick what kind of property it is.`, p.id);
    const seizedDrugs = p.type === '10' && p.loss === '6';
    if (!noneOrUnknown && !seizedDrugs && p.value == null && !p.valueUnknown) err('property', `${what}: enter its value (or tick “value unknown”).`, p.id);
    if (seizedDrugs && p.value != null && p.value > 0) err('property', `${what}: seized drugs aren’t given a dollar value. Leave the value blank.`, p.id);
    if (p.value != null && (p.value < 0 || p.value > 999_999_999)) err('property', `${what}: the value doesn’t look right.`, p.id);
    if (p.value != null && !Number.isInteger(p.value)) warn('property', `${what}: values are sent to the state in whole dollars (it will be rounded).`, p.id);
    if (p.loss === '5' && !p.recoveredOn) err('property', `${what}: when was it recovered?`, p.id);
    if (seizedDrugs) {
      if (!p.drug?.type) err('property', `${what}: what kind of drug?`, p.id);
      const pending = p.drug?.unit === 'XX';
      if (p.drug?.type !== 'X' && !pending && (p.drug?.qty == null || p.drug.qty <= 0)) err('property', `${what}: how much? Enter the amount and unit (or “not reported yet” if the lab is pending).`, p.id);
      if (p.drug?.type !== 'X' && p.drug?.qty != null && !p.drug.unit) err('property', `${what}: pick the unit (grams, pills, plants…).`, p.id);
    }
    if (VEHICLE_PROPERTY.includes(p.type) && p.loss === '7' && !p.plate && !p.vin) warn('property', `${what}: add the tag or VIN so it can be entered in NCIC.`, p.id);
    if (p.type === '13' && ['7', '5', '6'].includes(p.loss) && !p.serial) warn('property', `${what}: add the gun’s serial number.`, p.id);
  }
  const stolen = (t: string) => r.property.filter((p) => p.loss === '7' && p.type === t).reduce((s, p) => s + (p.value ?? 0), 0);
  for (const p of r.property.filter((p) => p.loss === '5' && p.value != null)) {
    const st = stolen(p.type);
    if (st > 0 && p.value! > st) warn('property', `${p.description || labelOf(PROPERTY_TYPES, p.type)}: the recovered value is more than the stolen value.`, p.id);
  }

  // Narrative & clearance (pp.101–103, 106)
  if (r.narrative.trim().length < 30) err('narrative', 'Write the narrative: what happened, in your words.');
  if (r.clearance.status === 'exceptional') {
    if (!r.clearance.reason) err('narrative', 'Exceptionally cleared: pick the reason.');
    if (!r.clearance.date) err('narrative', 'Exceptionally cleared: enter the date it was cleared.');
    else {
      const d = r.clearance.date;
      if (from && d < from.toISOString().slice(0, 10)) err('narrative', 'The clearance date is before the incident.');
      if (d > now.toISOString().slice(0, 10)) err('narrative', 'The clearance date is in the future.');
    }
    if (arrestees.length) err('narrative', 'Someone was arrested, so the case is cleared by arrest, not exceptionally.');
    if (reportable.length && reportable.every((o) => def(o.code)!.notCrime)) err('narrative', 'This isn’t a crime, so it can’t be cleared exceptionally. Mark it administratively closed instead.');
  }
  if (r.clearance.status === 'arrest' && !arrestees.length) err('people', 'The case is marked cleared by arrest. Add the arrested person.');
  if (arrestees.length && r.clearance.status === 'open') warn('narrative', 'Someone was arrested. Mark the case “Cleared by arrest” if that closes it.');
  // Narcan
  const n = r.narcan;
  if (n?.used) {
    if (!n.givenBy) err('what', 'Narcan was used: say who gave it.');
    if (n.givenBy === 'me' && !n.formDone) err('what', 'You gave Narcan: fill in the Narcan use form, keep a copy, then check “I completed the Narcan form”.');
  } else if (n?.used == null && narcanRelevant(r)) {
    warn('what', 'Overdose on this call: was Narcan used? Answer it on the first step.');
  }
  return out;
}

// ─── Summary (for lists, search and analytics) ──────────────────────────────

export interface ReportSummary {
  offenseCodes: string[];
  primary: string | null;
  title: string;
  category: string | null;
  againstPerson: boolean;
  nibrs: boolean;
  victims: number;
  suspects: number;
  arrests: number;
  stolenValue: number;
  recoveredValue: number;
  firearm: boolean;
  domestic: boolean;
  bias: boolean;
  names: string[];
  plates: string[];
  serials: string[];
}

const SEVERITY = ['09A', '09B', '100', '11A', '11B', '11C', '120', '13A', '11D', '36A', '36B', '64A', '64B', '200', '220', '240', '13B', '13C', '210'];

export function summarize(r: IncidentReport): ReportSummary {
  const codes = r.offenses.map((o) => o.code);
  const ranked = [...codes].sort((a, b) => {
    const ia = SEVERITY.indexOf(a), ib = SEVERITY.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  const primary = ranked[0] ?? null;
  const def = primary ? OFFENSE_BY_CODE.get(primary) : undefined;
  const suspects = r.people.filter(isSuspect);
  const victims = r.people.filter(isVictim);
  const sum = (loss: string) => r.property.filter((p) => p.loss === loss).reduce((s, p) => s + (p.value ?? 0), 0);
  return {
    offenseCodes: codes,
    primary,
    title: def ? def.plain + (codes.length > 1 ? ` + ${codes.length - 1} more` : '') : 'Incident',
    category: def?.category ?? null,
    againstPerson: codes.some((c) => OFFENSE_BY_CODE.get(c)?.against === 'person'),
    nibrs: codes.some((c) => OFFENSE_BY_CODE.get(c)?.group === 'A') || r.people.some(isArrestee),
    victims: victims.length,
    suspects: suspects.filter((s) => !s.unknown).length,
    arrests: r.people.filter(isArrestee).length,
    stolenValue: sum('7'),
    recoveredValue: sum('5'),
    firearm: r.offenses.some((o) => o.weapons.some((w) => FIREARM_WEAPONS.includes(w))),
    domestic: victims.some((v) => Object.values(v.relationships).some((x) => FAMILY_RELATIONSHIPS.includes(x))),
    bias: r.offenses.some((o) => !!o.bias && o.bias !== '88' && o.bias !== '99'),
    names: r.people.filter((p) => !p.unknown && p.name.trim()).map((p) => p.name.trim()),
    plates: r.property.map((p) => p.plate).filter((x): x is string => !!x),
    serials: r.property.map((p) => p.serial ?? p.vin).filter((x): x is string => !!x),
  };
}

// ─── Analytics ──────────────────────────────────────────────────────────────

export interface AnalyticsIncident {
  id: string;
  occurredAt: Date;
  primary: string | null;
  codes: string[];
  category: string | null;
  lat: number | null;
  lng: number | null;
  address: string;
  cleared: boolean;
  arrests: number;
  firearm: boolean;
  domestic: boolean;
  stolenValue: number;
}

export interface CrimeAnalytics {
  total: number;
  previous: number;
  changePct: number | null;
  violent: number;
  property: number;
  drugs: number;
  clearanceRate: number | null;
  arrests: number;
  firearm: number;
  domestic: number;
  stolenValue: number;
  byOffense: { code: string; label: string; count: number; previous: number }[];
  byCategory: { category: string; count: number }[];
  /** [day 0=Sun..6][hour 0..23] */
  dayHour: number[][];
  byWeek: { weekStart: string; count: number }[];
  hotSpots: { address: string; count: number; lat: number | null; lng: number | null }[];
}

export function crimeAnalytics(current: AnalyticsIncident[], previous: AnalyticsIncident[], tz: string, from: Date, to: Date): CrimeAnalytics {
  const countBy = <T,>(xs: T[], key: (x: T) => string | null) => {
    const m = new Map<string, number>();
    for (const x of xs) {
      const k = key(x);
      if (k) m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  };
  const crimes = current.filter((x) => x.category !== 'non-criminal');
  const prevCrimes = previous.filter((x) => x.category !== 'non-criminal');
  const offCount = new Map<string, number>();
  const prevOffCount = new Map<string, number>();
  for (const x of crimes) for (const c of new Set(x.codes)) if (OFFENSE_BY_CODE.get(c)?.group !== 'NC') offCount.set(c, (offCount.get(c) ?? 0) + 1);
  for (const x of prevCrimes) for (const c of new Set(x.codes)) if (OFFENSE_BY_CODE.get(c)?.group !== 'NC') prevOffCount.set(c, (prevOffCount.get(c) ?? 0) + 1);
  const dayHour = Array.from({ length: 7 }, () => new Array<number>(24).fill(0));
  for (const x of crimes) {
    const p = localParts(x.occurredAt, tz);
    dayHour[p.weekday]![p.hour]!++;
  }
  // Weeks (Sunday start) across the period, including empty ones.
  const weeks = new Map<string, number>();
  const weekOf = (d: Date) => {
    const day = localParts(d, tz).date;
    const dt = new Date(`${day}T12:00:00Z`);
    dt.setUTCDate(dt.getUTCDate() - dt.getUTCDay());
    return dt.toISOString().slice(0, 10);
  };
  for (let t = +from; t <= +to; t += 7 * 86_400_000) weeks.set(weekOf(new Date(t)), 0);
  weeks.set(weekOf(to), weeks.get(weekOf(to)) ?? 0);
  for (const x of crimes) weeks.set(weekOf(x.occurredAt), (weeks.get(weekOf(x.occurredAt)) ?? 0) + 1);
  const spots = new Map<string, { address: string; count: number; lat: number | null; lng: number | null }>();
  for (const x of crimes) {
    const k = x.address.trim().toUpperCase();
    if (!k) continue;
    const s = spots.get(k);
    if (s) s.count++;
    else spots.set(k, { address: x.address, count: 1, lat: x.lat, lng: x.lng });
  }
  const cat = (c: string) => crimes.filter((x) => x.codes.some((code) => OFFENSE_BY_CODE.get(code)?.category === c)).length;
  const clearable = crimes.filter((x) => x.codes.some((c) => OFFENSE_BY_CODE.get(c)?.group === 'A' && !OFFENSE_BY_CODE.get(c)?.notCrime));
  return {
    total: crimes.length,
    previous: prevCrimes.length,
    changePct: prevCrimes.length ? Math.round(((crimes.length - prevCrimes.length) / prevCrimes.length) * 100) : null,
    violent: crimes.filter((x) => x.codes.some((c) => ['violent', 'sex'].includes(OFFENSE_BY_CODE.get(c)?.category ?? '') && OFFENSE_BY_CODE.get(c)?.against === 'person') || x.codes.includes('120')).length,
    property: cat('theft') + crimes.filter((x) => !x.codes.some((c) => OFFENSE_BY_CODE.get(c)?.category === 'theft') && x.codes.some((c) => ['damage', 'fraud'].includes(OFFENSE_BY_CODE.get(c)?.category ?? ''))).length,
    drugs: cat('drugs'),
    clearanceRate: clearable.length ? Math.round((clearable.filter((x) => x.cleared).length / clearable.length) * 100) : null,
    arrests: crimes.reduce((s, x) => s + x.arrests, 0),
    firearm: crimes.filter((x) => x.firearm).length,
    domestic: crimes.filter((x) => x.domestic).length,
    stolenValue: crimes.reduce((s, x) => s + x.stolenValue, 0),
    byOffense: [...new Set([...offCount.keys(), ...prevOffCount.keys()])]
      .map((code) => ({ code, label: OFFENSE_BY_CODE.get(code)?.plain ?? code, count: offCount.get(code) ?? 0, previous: prevOffCount.get(code) ?? 0 }))
      .filter((x) => x.count > 0 || x.previous > 0)
      .sort((a, b) => b.count - a.count || b.previous - a.previous || a.label.localeCompare(b.label)),
    byCategory: [...countBy(crimes, (x) => x.category)].map(([category, count]) => ({ category, count })).sort((a, b) => b.count - a.count),
    dayHour,
    byWeek: [...weeks].sort(([a], [b]) => a.localeCompare(b)).map(([weekStart, count]) => ({ weekStart, count })),
    hotSpots: [...spots.values()].filter((s) => s.count > 1).sort((a, b) => b.count - a.count).slice(0, 10),
  };
}

// ─── Cleaning what the browser sends ────────────────────────────────────────

const str = (v: unknown, max = 200): string | null => (typeof v === 'string' && v.trim() ? v.slice(0, max) : null);
const strs = (v: unknown, maxItems = 5, maxLen = 4): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').map((x) => x.slice(0, maxLen)).slice(0, maxItems) : []);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() && Number.isFinite(Number(v)) ? Number(v) : null);
const iso = (v: unknown): string | null => (typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? new Date(v).toISOString() : null);
const id = (v: unknown, i: number, p: string) => (typeof v === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(v) ? v : `${p}${i + 1}`);
const ROLES: PersonRole[] = ['victim', 'suspect', 'arrestee', 'witness', 'complainant', 'other'];

/** Any stored or sent value → a valid report (unknown fields dropped, sizes capped). */
export function normalizeReport(v: unknown): IncidentReport {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, any>;
  const loc = (o.location && typeof o.location === 'object' ? o.location : {}) as Record<string, unknown>;
  const cl = (o.clearance && typeof o.clearance === 'object' ? o.clearance : {}) as Record<string, unknown>;
  return {
    v: 1,
    occurredFrom: iso(o.occurredFrom),
    occurredTo: iso(o.occurredTo),
    occurredUnknown: o.occurredUnknown === 'date' || o.occurredUnknown === 'hour' ? o.occurredUnknown : null,
    reportedAt: iso(o.reportedAt),
    cargoTheft: typeof o.cargoTheft === 'boolean' ? o.cargoTheft : null,
    location: {
      line1: str(loc.line1, 200) ?? '',
      city: str(loc.city, 80),
      state: str(loc.state, 2),
      zip: str(loc.zip, 10),
      lat: num(loc.lat),
      lng: num(loc.lng),
      type: str(loc.type, 3),
      detail: str(loc.detail, 300),
    },
    offenses: (Array.isArray(o.offenses) ? o.offenses : []).slice(0, 10).map((x: any, i: number) => ({
      id: id(x?.id, i, 'o'),
      code: str(x?.code, 12) ?? '',
      attempted: typeof x?.attempted === 'boolean' ? x.attempted : null,
      weapons: strs(x?.weapons, 3, 3),
      circumstances: strs(x?.circumstances, 2, 2),
      justifiable: str(x?.justifiable, 1),
      criminalActivity: strs(x?.criminalActivity, 3, 1),
      gang: str(x?.gang, 1),
      suspectedUsing: strs(x?.suspectedUsing, 3, 1),
      bias: str(x?.bias, 2),
      entry: x?.entry === 'F' || x?.entry === 'N' ? x.entry : null,
      premises: num(x?.premises),
      special: strs(x?.special, 3, 1),
    })),
    people: (Array.isArray(o.people) ? o.people : []).slice(0, 60).map((x: any, i: number) => ({
      id: id(x?.id, i, 'p'),
      roles: strs(x?.roles, 6, 12).filter((r): r is PersonRole => (ROLES as string[]).includes(r)),
      type: str(x?.type, 1) ?? '',
      leoka: x?.leoka && typeof x.leoka === 'object' ? { activity: str(x.leoka.activity, 2), assignment: str(x.leoka.assignment, 1), ori: str(x.leoka.ori, 9) } : null,
      unknown: x?.unknown === true,
      name: str(x?.name, 120) ?? '',
      last: str(x?.last, 60),
      first: str(x?.first, 60),
      middle: str(x?.middle, 60),
      suffix: str(x?.suffix, 10),
      dob: typeof x?.dob === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x.dob) ? x.dob : null,
      age: str(x?.age, 7),
      sex: str(x?.sex, 1),
      race: str(x?.race, 1),
      ethnicity: str(x?.ethnicity, 1),
      resident: str(x?.resident, 1),
      phone: str(x?.phone, 40),
      address: str(x?.address, 200),
      description: str(x?.description, 500),
      injuries: strs(x?.injuries, 5, 1),
      relationships: Object.fromEntries(
        Object.entries(x?.relationships && typeof x.relationships === 'object' ? x.relationships : {})
          .filter(([k, val]) => /^[A-Za-z0-9_-]{1,40}$/.test(k) && typeof val === 'string')
          .slice(0, 20)
          .map(([k, val]) => [k, (val as string).slice(0, 2)]),
      ),
      victimOf: strs(x?.victimOf, 10, 40),
      height: num(x?.height),
      weight: num(x?.weight),
      hair: str(x?.hair, 20),
      eyes: str(x?.eyes, 20),
      marks: str(x?.marks, 500),
      unknownTraits: strs(x?.unknownTraits, 4, 10).filter((t) => ['height', 'weight', 'hair', 'eyes'].includes(t)),
      photoId: typeof x?.photoId === 'string' && /^[0-9a-f-]{36}$/.test(x.photoId) ? x.photoId : null,
      arrest:
        x?.arrest && typeof x.arrest === 'object'
          ? {
              at: iso(x.arrest.at),
              type: str(x.arrest.type, 1),
              charge: str(x.arrest.charge, 12),
              warrant: str(x.arrest.warrant, 60),
              armed: strs(x.arrest.armed, 2, 3),
              juvenile: str(x.arrest.juvenile, 1),
              drugActivity: str(x.arrest.drugActivity, 1),
              drugType: str(x.arrest.drugType, 1),
              bookingNumber: str(x.arrest.bookingNumber, 40),
              facility: str(x.arrest.facility, 120),
              localCharges: (Array.isArray(x.arrest.localCharges) ? x.arrest.localCharges : []).slice(0, 30).map((c: any) => ({ statute: str(c?.statute, 40) ?? '', description: str(c?.description, 200) ?? '', warrant: str(c?.warrant, 60), cdr: str(c?.cdr, 10) })),
            }
          : null,
    })),
    property: (Array.isArray(o.property) ? o.property : []).slice(0, 200).map((x: any, i: number) => ({
      id: id(x?.id, i, 'i'),
      loss: str(x?.loss, 1) ?? '',
      type: str(x?.type, 2) ?? '',
      description: str(x?.description, 300) ?? '',
      value: num(x?.value),
      valueUnknown: x?.valueUnknown === true,
      quantity: num(x?.quantity),
      make: str(x?.make, 60),
      model: str(x?.model, 60),
      serial: str(x?.serial, 60),
      color: str(x?.color, 40),
      plate: str(x?.plate, 15),
      vin: str(x?.vin, 20),
      year: str(x?.year, 4),
      owner: str(x?.owner, 120),
      drug: x?.drug && typeof x.drug === 'object' ? { type: str(x.drug.type, 1), qty: num(x.drug.qty), unit: str(x.drug.unit, 2) } : null,
      recoveredOn: typeof x?.recoveredOn === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x.recoveredOn) ? x.recoveredOn : null,
      evidence: x?.evidence === true,
    })),
    narrative: typeof o.narrative === 'string' ? o.narrative.slice(0, 50_000) : '',
    clearance: {
      status: ['open', 'arrest', 'exceptional', 'unfounded', 'closed'].includes(String(cl.status)) ? (cl.status as IncidentReport['clearance']['status']) : 'open',
      reason: str(cl.reason, 1),
      date: typeof cl.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(cl.date) ? cl.date : null,
    },
    related: str(o.related, 300),
    narcan:
      o.narcan && typeof o.narcan === 'object'
        ? {
            used: typeof o.narcan.used === 'boolean' ? o.narcan.used : null,
            givenBy: ['me', 'deputy', 'ems', 'other'].includes(o.narcan.givenBy) ? o.narcan.givenBy : null,
            doses: num(o.narcan.doses),
            formDone: o.narcan.formDone === true,
          }
        : null,
    societyVictim: o.societyVictim === true,
  };
}

/** Everyone under 18 on a report (by date of birth on the day it happened, or the age given), in any role. */
export function juvenilesIn(people: ReportPerson[], on: Date): { id: string; name: string; roles: PersonRole[]; age: number }[] {
  const out: { id: string; name: string; roles: PersonRole[]; age: number }[] = [];
  for (const p of people) {
    let age = ageOn(p.dob, on);
    if (age == null && p.age) {
      const n = Number(/^\s*(\d{1,3})/.exec(p.age)?.[1]);
      if (Number.isFinite(n)) age = n;
    }
    if (age != null && age < 18) out.push({ id: p.id, name: p.name || [p.first, p.last].filter(Boolean).join(' ') || 'Unknown', roles: p.roles, age });
  }
  return out;
}
